import { usePath } from '@cast/utils';

import { CenteredLogoLoader } from 'components/loaders';
import { Navigate } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { ReliabilityChartSection } from 'features/cluster/_components/reliability/_components/ReliabilityChartSection';
import { TwoColumnChartLayout } from 'features/cluster/workload-optimization/_components/TwoColumnChartLayout';
import { ReliabilityMetricsNotInstalled } from 'features/cluster/_components/reliability/_components/ReliabilityMetricsNotInstalled';
import { ReliabilityNoData } from 'features/cluster/_components/reliability/_components/ReliabilityNoData';
import { ReliabilitySummarySection } from 'features/cluster/_components/reliability/_components/ReliabilitySummarySection';
import { useReliabilityCapabilities } from 'features/cluster/_components/reliability/_hooks/useReliabilityCapabilities';
import { useReliabilityData } from 'features/cluster/_components/reliability/_hooks/useReliabilityData';
import { makeWorkloadsComputeCostLink } from 'features/cluster/cost-report/links';
import { CostReportPageContainer } from 'features/cost-report/components/CostReportPageContainer';
import { reliabilityFilterPresets } from 'features/cost-report/constants';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { CostReportProvider } from 'features/cost-report/providers/CostReportProvider';
import { CostReportTab, NoDataType } from 'features/cost-report/types/common';
import { useIsReliabilityMetricsAvailableInBeta } from 'hooks/permissions/useIsReliabilityMetricsAvailableInBeta';
import { useParams } from 'hooks/useParams';
import { DateRangeFilter } from '@cast/shared-date-ui';

import { useGetWorkloadDetailsEnabledTabs } from '../_hooks/useGetWorkloadDetailsEnabledTabs';
import { WorkloadDetailsRouteProps } from '../_types';
import { WorkloadDetailsTitleWithActions } from '../components/WorkloadDetailsTitleWithActions';
import { ReliabilityDailyDetailsTable } from './_components/ReliabilityDailyDetailsTable';

const ReliabilityDetails = () => {
  const { workloadName, workloadType, namespace } =
    useParams<WorkloadDetailsRouteProps>();
  const detailsTabs = useGetWorkloadDetailsEnabledTabs();
  const {
    granularDateRange,
    timezone,
    currentDateRange,
    currentDateRangeFilter,
  } = useCostReportContext();
  const {
    showNotInstalled,
    hasReliabilityMetrics,
    refetchCapabilities,
    capabilitiesError,
    isCapabilitiesLoading,
  } = useReliabilityCapabilities();
  const isReliabilityEnabled = useIsReliabilityMetricsAvailableInBeta();
  const computeCostLink = usePath(makeWorkloadsComputeCostLink);

  const {
    data,
    previousPeriodData,
    dailyTableData,
    chartData,
    trafficMetrics,
    latencyMetrics,
    isLoading,
    isDailyTableLoading,
    showNoData,
    error,
    refetch,
  } = useReliabilityData({
    namespace,
    workloadType,
    workloadName,
    granularDateRange,
    timezone,
    currentDateRange,
    currentDateRangeFilter,
    hasReliabilityMetrics,
  });

  if (!isReliabilityEnabled) {
    return <Navigate to={computeCostLink} replace />;
  }

  const isPageLoading =
    isCapabilitiesLoading || isLoading || isDailyTableLoading;

  const showReliabilityNoData =
    !showNotInstalled && !isPageLoading && showNoData;

  return (
    <CostReportPageContainer
      currentTab={CostReportTab.RELIABILITY}
      {...detailsTabs}
      title={
        <WorkloadDetailsTitleWithActions
          workloadName={workloadName}
          namespace={namespace}
          workloadType={workloadType}
        />
      }
      errorMessage="Failed to load workload reliability metrics"
      hasError={!!error || !!capabilitiesError}
      refetch={() => {
        refetch();
        refetchCapabilities();
      }}
      noPriceTypeSelector
      showNoData={showNotInstalled ? false : showReliabilityNoData}
      noDataType={NoDataType.RELIABILITY}
      noDataComponent={
        showReliabilityNoData ? (
          <ReliabilityNoData variant="workload" />
        ) : undefined
      }
      loadingMessage={
        isPageLoading && !showNotInstalled ? <CenteredLogoLoader /> : undefined
      }
    >
      {showNotInstalled ? (
        <ReliabilityMetricsNotInstalled />
      ) : isPageLoading ? null : (
        <TwoColumnChartLayout
          summary={
            <ReliabilitySummarySection
              data={data}
              previousPeriodData={previousPeriodData}
            />
          }
          leftChart={
            <ReliabilityChartSection
              title="Traffic"
              metrics={trafficMetrics}
              chartData={chartData}
              isLoading={isLoading || isCapabilitiesLoading}
              data={data}
              timezone={timezone}
              testId="workload-reliability-traffic-chart"
              syncId="workload-reliability"
            />
          }
          rightChart={
            <ReliabilityChartSection
              title="Latency"
              metrics={latencyMetrics}
              chartData={chartData}
              isLoading={isLoading || isCapabilitiesLoading}
              data={data}
              timezone={timezone}
              testId="workload-reliability-latency-chart"
              syncId="workload-reliability"
            />
          }
          bottomContent={
            <ReliabilityDailyDetailsTable
              data={dailyTableData}
              timezone={timezone}
              isLoading={isDailyTableLoading || isCapabilitiesLoading}
              cacheKey={
                tableCacheKeys.COST_MONITORING.WORKLOAD.DETAILS.RELIABILITY
              }
              testId="workload-reliability-daily-details"
            />
          }
        />
      )}
    </CostReportPageContainer>
  );
};

export const ReliabilityDetailsWithProviders = () => {
  return (
    <CostReportProvider
      rangePickerProps={{
        filterPresets: reliabilityFilterPresets,
        initialPreset: DateRangeFilter.ThisMonth,
      }}
    >
      <ReliabilityDetails />
    </CostReportProvider>
  );
};
